import { Sensor } from '../../domain/entities/Sensor';
import { UmbralAnalogico, UmbralDigital, type Umbral } from '../../domain/entities/Umbral';
import { Criticidad } from '../../domain/value-objects/Criticidad';
import { Dominio } from '../../domain/value-objects/Dominio';
import { Lectura } from '../../domain/value-objects/Lectura';
import { aBooleano, aFecha, aNumero, aTexto } from './conversiones';

/**
 * Fila de la vista `vw_sensores_detallado`.
 * Los nombres son los reales de la vista, comprobados contra la base de datos:
 * el sensor se llama `nombre_sensor`, no `sensor_nombre` como en otras vistas.
 */
export interface FilaSensor extends Record<string, unknown> {
  sensor_id?: unknown;
  nombre_sensor?: unknown;
  /** Texto: `Digital` o `Analógico`. */
  tipo_sensor?: unknown;
  unidad_simbolo?: unknown;
  codigo_envio?: unknown;
  forma_operacion?: unknown;
  /** `activo` o `inactivo`. */
  estado?: unknown;
  nodo_id?: unknown;
  nodo_nombre?: unknown;
  area_id?: unknown;
  area_nombre?: unknown;
  cliente_id?: unknown;
  cliente_nombre?: unknown;
}

/**
 * Fila de `umbrales_analogicos`.
 * El umbral define una banda mín/máx; no hay niveles críticos aparte.
 */
export interface FilaUmbralAnalogico extends Record<string, unknown> {
  id?: unknown;
  sensor_id?: unknown;
  valor_min?: unknown;
  valor_max?: unknown;
  /** Banda muerta. */
  deadband?: unknown;
  debounce_segundos?: unknown;
  inhibicion_arranque_segundos?: unknown;
  criticidad?: unknown;
  activo?: unknown;
  latched?: unknown;
  notificar?: unknown;
  mensaje?: unknown;
}

/** Fila de `umbrales_digitales`. */
export interface FilaUmbralDigital extends Record<string, unknown> {
  id?: unknown;
  sensor_id?: unknown;
  estado_esperado?: unknown;
  debounce_segundos?: unknown;
  criticidad?: unknown;
  activo?: unknown;
  latched?: unknown;
  notificar?: unknown;
  mensaje?: unknown;
}

/** Fila de `last_sensor_values`. */
export interface FilaUltimoValor extends Record<string, unknown> {
  sensor_id?: unknown;
  valor?: unknown;
  measured_at?: unknown;
  received_at?: unknown;
}

function comun(fila: FilaUmbralAnalogico | FilaUmbralDigital, sensorId: number) {
  return {
    id: aNumero(fila.id) ?? 0,
    sensorId,
    criticidad: Criticidad.from(aTexto(fila.criticidad)),
    activo: aBooleano(fila.activo) ?? true,
    retardoSegundos: aNumero(fila.debounce_segundos) ?? 0,
    retencion: aBooleano(fila.latched) ?? false,
    notificaciones: aBooleano(fila.notificar) ?? false,
    actuadoresVinculados: [] as number[],
  };
}

export function aUmbralAnalogico(fila: FilaUmbralAnalogico, sensorId: number): UmbralAnalogico {
  return new UmbralAnalogico({
    ...comun(fila, sensorId),
    valorMinimo: aNumero(fila.valor_min),
    valorMaximo: aNumero(fila.valor_max),
    histeresis: aNumero(fila.deadband),
    inhibicionArranqueSegundos: aNumero(fila.inhibicion_arranque_segundos) ?? 0,
  });
}

export function aUmbralDigital(fila: FilaUmbralDigital, sensorId: number): UmbralDigital {
  return new UmbralDigital({
    ...comun(fila, sensorId),
    estadoEsperado: aBooleano(fila.estado_esperado),
    horarios: [],
  });
}

export interface ExtrasSensor {
  /** Categoría, que la vista de sensores no incluye. */
  categoria?: string | null;
  umbral?: Umbral | null;
  lectura?: Lectura | null;
}

/**
 * Convierte una fila de `vw_sensores_detallado` en entidad de dominio.
 *
 * El umbral, la última lectura y la categoría llegan de consultas aparte
 * porque viven en otras tablas; el repositorio los compone antes de llamar
 * aquí.
 */
export function aSensor(fila: FilaSensor, extras: ExtrasSensor = {}): Sensor {
  const id = aNumero(fila.sensor_id) ?? 0;
  const tipo = aTexto(fila.tipo_sensor);
  const esDigital = (tipo ?? '').toLowerCase().startsWith('digital');

  return new Sensor({
    id,
    nombre: aTexto(fila.nombre_sensor) ?? `Sensor ${id}`,
    esDigital,
    // La vista sólo trae el símbolo; el nombre largo de la unidad vive en el
    // catálogo de tipos de sensor y no hace falta para mostrar la lectura.
    unidad: null,
    simbolo: aTexto(fila.unidad_simbolo),
    tipoNombre: tipo,
    codigoEnvio: aTexto(fila.codigo_envio),
    activo: aTexto(fila.estado) === 'activo',
    dominio: Dominio.from(extras.categoria ?? null),
    nodoId: aNumero(fila.nodo_id),
    nodoNombre: aTexto(fila.nodo_nombre),
    areaId: aNumero(fila.area_id),
    areaNombre: aTexto(fila.area_nombre),
    clienteId: aNumero(fila.cliente_id),
    clienteNombre: aTexto(fila.cliente_nombre),
    decimales: 2,
    lectura: extras.lectura ?? null,
    umbral: extras.umbral ?? null,
  });
}

/** Construye la lectura a partir de una fila de `last_sensor_values`. */
export function aLectura(fila: FilaUltimoValor): Lectura | null {
  return Lectura.crear(fila.valor, aFecha(fila.measured_at) ?? aFecha(fila.received_at));
}
